import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import config from '../config';
import { isSocialErrorResponse } from './socialResponse';
import { isLocalhost } from './urlHelpers';

// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

export const apiBaseUrl = () => {
    if (isLocalhost()) {
        const port = process.env.REACT_APP_DEV_API_SERVER_PORT || process.env.PORT || '8080';
        return `http://localhost:${port}`;
    }

    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return window.location.origin;
    }

    return process.env.REACT_APP_CANONICAL_ROOT_URL;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
    // Use Decimal type instead of SDK's BigDecimal.
    {
        type: sdkTypes.BigDecimal,
        customType: Decimal,
        writer: v => new sdkTypes.BigDecimal(v.toString()),
        reader: v => new Decimal(v.value),
    },
];

const serialize = data => {
    return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = str => {
    return transit.read(str, { typeHandlers });
};

const sendAsJSON = body => ({
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
    },
    body: typeof body === 'string' ? body : JSON.stringify(body),
});

const httpMeth = (path, options) =>
    fetch(`${apiBaseUrl()}${path}`, options)
        .then(res => res)
        .catch(e => ({ error: true, message: e.message || 'The http request failed' }));

const get = path =>
    fetch(`${apiBaseUrl()}${path}`)
        .then(res => res)
        .catch(e => ({ error: true, message: e.message || 'fetch.get call failed' }));

const post = (path, body, optionsProp) => {
    const url = `${apiBaseUrl()}${path}`;
    const options = {
        method: 'POST',
        credentials: isLocalhost() ? 'omit' : 'include',
        headers: {
            'Content-Type': 'application/transit+json',
        },
        body: serialize(body),
    };
    return fetch(url, optionsProp || options)
        .then(res => {
            const contentTypeHeader = res.headers.get('Content-Type');
            const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
            const isTextPlain = contentTypeHeader === 'text/plain; charset=utf-8';

            if (res.status >= 400) {
                if (isTextPlain) {
                    return res.text().then(resText => {
                        throw { message: resText, status: res.status };
                    });
                }
                return res
                    .json()
                    .then(data => {
                        let e = new Error();
                        e = Object.assign(e, data);
                        throw e;
                    })
                    .catch(e => e);
            }
            if (contentType === 'application/transit+json') {
                return res.text().then(deserialize);
            } else if (contentType === 'application/json') {
                return res.json();
            }
            return res.text();
        })
        .catch(e => {
            return e;
        });
};

export const handleHttpAction = async (path, options) => httpMeth(path, options);

export const transitionPrivileged = body => {
    return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
    return post('/api/auth/create-user-with-idp', body);
};

export const createUserWithNativeIdp = body => {
    return post('/api/auth/create-user-with-native-idp', body);
};

export const getPolygonCoordinates = body => {
    return post('/api/map/polygon', body);
};

export const denormalize = (key, dataToNormalize) =>
    dataToNormalize.reduce(
        (acc, item) => {
            acc.ids.push(item[key]);
            acc.data[item[key]] = { ...item };
            return acc;
        },
        { ids: [], data: {} }
    );

export const createCreditsPurchasingPaylink = body =>
    post('/api/payrexx/user-services/credits-purchasing-paylink', body);

export const getMatchingScore = body => post('/api/matching-matrix/calculate-score', body);

export const getMatchingScoreBulk = body => post('/api/matching-matrix/calculate-score-bulk', body);

export const sortByMatchingScore = body => post('/api/matching-matrix/sort-by-score', body);

export const getMarketplaceReviews = async perPage =>
    get(`/api/reviews/marketplace-reviews/${perPage}`);

export const requestEntityAssetsList = async (id, variant) =>
    get(`/api/entity-assets/${variant}/${id}/list`);

export const updateEntityAssetsMetadata = async body =>
    post('/api/entity-assets/update-metadata', null, sendAsJSON(body));

export const rotateEntityImage = async body =>
    post('/api/entity-assets/rotate-image', null, sendAsJSON(body));

export const removeSingleAsset = async body =>
    post('/api/entity-assets/remove-single', null, sendAsJSON(body));

/** filetypes = /png|jpeg|jpg|mp4|ogg|mov|txt|pdf|docx/ */
export const uploadSingleFile = async body =>
    post('/api/entity-assets/files', null, {
        method: 'POST',
        body,
    });

/** filetypes = /png|jpeg|jpg|mp4|mov/ */
export const uploadSingleMedia = async body =>
    post('/api/entity-assets/media', null, {
        method: 'POST',
        body,
    });

export const requestSuperDeals = async () => get('/api/super-deals/list');
export const requestClubsOffers = async () => get('/api/clubs-offers/list');

export const updateUserVisitorsData = async (
    userId,
    visitedEntryId,
    visitorRepresentationId,
    entryType
) =>
    get(
        `/api/credits/insert-visitors-data/${userId}/${visitedEntryId}/${visitorRepresentationId}/${entryType}`
    );

export const updateCreditsHistory = async body =>
    post('/api/credits/update', null, sendAsJSON(body));

export const revealUserVisitors = async (visitedEntryId, visitorRepresentationId) =>
    get(`/api/credits/reveal-visitors/${visitedEntryId}/${visitorRepresentationId}`);

export const updateInAppPurchasePackages = async (transactionId, listingId) =>
    get(`/api/app-store/packages/${transactionId}/${listingId}`);

export const updateInAppPurchaseCredits = async transactionId =>
    get(`/api/app-store/credits/${transactionId}`);

export const withdrawUserCredits = async (userId, creditsNum, isSponsored) =>
    get(`/api/credits/withdraw/${userId}/${creditsNum}/${isSponsored ? 'sponsored' : 'ordinary'}`);

export const sponsoredTransactionAllowed = async userId =>
    get(`/api/credits/sponsored-allowed/${userId}`);

export const getAllUsersExternalReviews = async currentUserId =>
    get(`/api/reviews/external/all/${currentUserId}`);

export const getUserExternalReviews = async userReviewedId =>
    get(`/api/reviews/external/${userReviewedId}`);

export const addUserExternalReviews = async body =>
    post('/api/reviews/external/add', null, sendAsJSON(body));

export const reportListing = async body =>
    post(`/api/reporting/suspicious-listing`, null, sendAsJSON(body));

export const reportFeedbackOnSection = async body =>
    post('/api/reporting/section', null, sendAsJSON(body));

export const sendRecommendations = async body =>
    post('/api/recommendations/recommend', null, sendAsJSON(body));

export const blockUser = async uuid => get(`/api/sdk-users/block-user/${uuid}`);

// export const getCurrentUserUpdates = async () => get('/api/sdk-users/check-updates');

export const deleteUserAvatar = async uuid => get(`/api/sdk-users/delete-avatar/${uuid}`);

export const updateUserViewsNumber = async uuid => get(`/api/sdk-users/views/${uuid}`);

export const getUsersClubs = async () => get(`/api/sdk-users/clubs`);

export const getUsersClubsByChars = async body =>
    post('/api/sdk-users/clubs', null, sendAsJSON(body));

export const getRecentTransactions = async createdAtEnd => {
    const query = createdAtEnd ? `?createdAtEnd=${createdAtEnd}` : '';
    return get(`/api/sdk-transactions/recent-transactions${query}`);
};

export const initiateUnlocking = async transactionId =>
    get(`/api/sdk-transactions/initiate-unlocking/${transactionId}`);

export const getContactInfoShared = async transactionId =>
    get(`/api/sdk-transactions/contact-info/${transactionId}`);

export const deleteTransactionMessage = async (messageId, transactionId) =>
    httpMeth(`/api/sdk-transactions/messages/${messageId}/${transactionId}`, {
        method: 'DELETE',
    });

export const getTransactionMessages = async transactionId =>
    get(`/api/transactions/${transactionId}/messages`);

export const sendTransactionMessageFile = async (transactionId, body, page = 1) =>
    post(
        `/api/transactions/${transactionId}/messages?page=${page}`,
        {},
        {
            method: 'POST',
            body,
        }
    );

/**
 * Current counters are sent along so the server can skip reading the listing.
 * See the endpoint for why trusting them is acceptable.
 * Without counters the server changes nothing and responds with 304.
 */
export const updateListingViewsNumber = (listingId, query) => {
    return get(`/api/sdk-listings/views/${listingId}?${query}`);
};

export const updateListingDataOnWishlistAction = async (listingId, userId, action) =>
    get(`/api/sdk-listings/favorites/${listingId}/${userId}/${action}`);

export const reflectRirderAvailabilityStatusToListing = async userId =>
    get(`/api/sdk-listings/reflect-availability/${userId}`);

export const updateListingsImpressions = async body =>
    post(`/api/sdk-listings/impressions`, null, sendAsJSON(body));

export const getListingsImpressions = async listingId =>
    get(`/api/sdk-listings/impressions/${listingId}`);

export const approveListing = async listingId =>
    httpMeth(`/api/sdk-listings/pending-state/${listingId}`, {
        method: 'PATCH',
    });
export const getMarketplaceListings = async queryParams => get(`/api/sdk-listings?${queryParams}`);

export const duplicateListingAssets = async entityId =>
    get(`/api/sdk-listings/duplicate/${entityId}`);

export const getAppointment = async uuid => get(`/api/appointment/${uuid}`);

export const createAppointment = async body =>
    post('/api/appointment/create', null, sendAsJSON(body));

export const updateAppointment = async ({ transactionId, statusUpdated }) =>
    httpMeth(`/api/appointment/${transactionId}/${statusUpdated}/change-status`, {
        method: 'PATCH',
    });
/**
 * Request marketplace public events
 * @param {Boolean} isInitial - first request on mount (wider time window) or a polling one
 * @returns Promise
 */
export const getRecentMarketplaceEvents = async isInitial =>
    get(`/api/events-public?isInitial=${Boolean(isInitial)}`);

export const sendSGEmail = async body => post('/api/emails/send', null, sendAsJSON(body));

export const sendTxSGEmail = async body => post('/api/emails/transaction', null, sendAsJSON(body));

export const getUnsubscribeGroupsInfo = async () => get(`/api/notifications/sg`);

export const addSGSuppressionByGroupId = async body =>
    post(`/api/notifications/sg`, null, sendAsJSON(body));

export const deleteSGSuppressionByGroupId = async groupId =>
    handleHttpAction(`/api/notifications/sg/${groupId}`, {
        method: 'DELETE',
    });

export const sendVerificationCode = async body => post(`/api/otp`, null, sendAsJSON(body));

export const getUnreadMessages = async uuid => get(`/api/unread-messages/${uuid}/messages`);

export const getPlaceDetailsData = async placeId => get(`/api/google-services/details/${placeId}`);
export const setPlaceDetailsData = async body =>
    post('/api/google-services/details', null, sendAsJSON(body));

export const getPredictionsData = async (searchStr, countryCode) =>
    get(`/api/google-services/predictions/${searchStr}/${countryCode}`);

export const getLocationsByPostalCode = async (countryCode, postalCode) =>
    get(`/api/locations/${encodeURIComponent(countryCode)}/${encodeURIComponent(postalCode)}`);

export const setPredictionsData = async body =>
    post('/api/google-services/predictions', null, sendAsJSON(body));

export const getOneSignalSubscriptionsInfo = async () => get(`/api/notifications/os`);
export const getFlipAndWinCards = async () => get('/api/flip-and-win');

export const getFlipAndWinMonthPrizes = async userCountry =>
    get(`/api/flip-and-win/prizes/month/${userCountry}`);

export const getFlipAndWinCredits = async cardId => get(`/api/flip-and-win/${cardId}`);

export const evaluateFlipOutcome = async (cardIds, cardIndex) => {
    const payload = Array.isArray(cardIds) ? cardIds : [cardIds];

    return handleHttpAction('/api/flip-and-win/batch-flip', {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cardIds: payload, cardIndex }),
    });
};
export const getUserNotifications = async () => get('/api/notifications/custom');

export const getUserNotificationsPublic = async userCountry =>
    get(`/api/notifications/custom/public/${userCountry}`);

export const updateUserNotificationsViewedAt = async data =>
    handleHttpAction('/api/notifications/custom/public/viewedAt', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });

export const updateUserNotificationsInteraction = async notificationId =>
    handleHttpAction(`/api/notifications/custom/public/${notificationId}`, {
        method: 'PATCH',
    });

export const sendAiPrompt = async body => post('/api/openai/prompt', null, sendAsJSON(body));

// ---- Social media automation (admin) ----

// Most social admin calls are fast metadata I/O (long work — publishing,
// generation, the Testlauf — runs in background jobs on the server), so a
// request that hangs past 20s is a fault — abort it instead of letting the
// button spin until some gateway kills the connection. The calls that wait on
// external providers server-side (a reviewer re-render fetching CDN photos;
// health/account pickers bounded by the adapters' own HTTP timeouts) pass
// their own `timeoutMs` above that worst case.
const SOCIAL_REQUEST_TIMEOUT_MS = 20000;
const SOCIAL_RERENDER_TIMEOUT_MS = 60000;

/**
 * The ONE timed social fetch: it performs the request AND reads its JSON body
 * under a single abort deadline, then applies the shared error contract.
 *
 * `fetch` resolves the moment the response HEADERS arrive, so a deadline that
 * ends there does not bound the call at all. The previous shape cleared the
 * abort timer in a `finally` and only then awaited `res.json()`, which left the
 * body completely unprotected: headers followed by a stalled body hung forever,
 * and every Redux/local loading flag set before the call — settings save,
 * workflow save/publish, post detail, publish marking, Testlauf polling — stayed
 * on for the life of the tab with no way back. Here the timer is cleared only
 * after the body has been consumed, so `timeoutMs` is the real end-to-end
 * budget. Both the JSON and the multipart helper go through this one function
 * so the guarantee cannot drift between them.
 *
 * @param {string} url absolute URL
 * @param {Object} options fetch options — this function owns `signal`
 * @param {number} timeoutMs end-to-end budget covering headers AND body
 * @returns {Promise<Object>} the parsed JSON body ({} when there is no JSON)
 * @throws {Error} the German timeout message on abort; otherwise the server's
 *   German message carrying `.status` and `.code`
 */
const timedSocialFetch = async (url, options, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { ...options, signal: controller.signal });
        // A body that is not JSON is a normal outcome (a 204, a proxy's HTML
        // error page) and must not hide the status. A body aborted mid-stream is
        // NOT: that is the deadline firing, and it has to surface as the timeout
        // instead of being swallowed into an empty success object.
        let json;
        try {
            json = await res.json();
        } catch (parseError) {
            if (parseError && parseError.name === 'AbortError') throw parseError;
            json = {};
        }
        if (isSocialErrorResponse(res.status, json)) {
            const error = new Error(json.message || `Request failed (${res.status})`);
            error.status = res.status;
            error.code = json.code || null;
            throw error;
        }
        return json;
    } catch (e) {
        throw e && e.name === 'AbortError'
            ? new Error('Zeitüberschreitung — der Server hat nicht geantwortet.')
            : e;
    } finally {
        clearTimeout(timer);
    }
};

const socialRequest = (
    path,
    { method = 'GET', body, timeoutMs = SOCIAL_REQUEST_TIMEOUT_MS } = {}
) => {
    const options = {
        method,
        credentials: isLocalhost() ? 'omit' : 'include',
        headers: { 'Content-Type': 'application/json' },
    };
    if (body !== undefined) options.body = JSON.stringify(body);

    return timedSocialFetch(`${apiBaseUrl()}${path}`, options, timeoutMs);
};

export const getSocialPosts = (status, page = 1, workflow, perPage) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (workflow) params.set('workflow', workflow);
    params.set('page', page);
    if (perPage) params.set('perPage', perPage);
    return socialRequest(`/api/social-posts?${params.toString()}`);
};

// Human reviewer queue: all pending approvals plus only Instagram Stories that
// are explicitly parked for manual action. Community-extension rows never enter
// this contract, and the server applies the distinction before pagination.
export const getSocialReviewerQueue = (page = 1, perPage = 100) => {
    const params = new URLSearchParams();
    params.set('queue', 'reviewer');
    params.set('page', page);
    params.set('perPage', perPage);
    return socialRequest(`/api/social-posts?${params.toString()}`);
};

export const getSocialPost = id => socialRequest(`/api/social-posts/${id}`);

export const deleteSocialPost = id =>
    socialRequest(`/api/social-posts/${id}`, { method: 'DELETE' });

// Request publishing of one or more channels. The server MARKS them and answers
// 202 immediately (external Postiz/Storrito I/O runs in the background) — callers
// poll the post until the requested channels settle.
// `channels` = [{ platform, content?, comment? }]. `dedicatedChannelAction`
// identifies one channel's own button; it may unlock a reconciled Postiz retry or
// a draft-held Storrito Story. Bulk publishing never carries that authority, even
// when there happens to be only one open channel.
export const requestSocialPublish = (id, channels, { dedicatedChannelAction = false } = {}) =>
    socialRequest(`/api/social-posts/${id}/publish`, {
        method: 'POST',
        body: { channels, dedicatedChannelAction },
    });

export const rerenderSocialChannel = (id, platform, doc) =>
    socialRequest(`/api/social-posts/${id}/channels/${platform}/rerender`, {
        method: 'POST',
        body: { doc },
        // Server-side raster fetches the source photo from the CDN (bounded at
        // 15s per image) — give it room instead of aborting a healthy render.
        timeoutMs: SOCIAL_RERENDER_TIMEOUT_MS,
    });

export const resolveSocialPostizOutcome = (id, platform, resolution) =>
    socialRequest(`/api/social-posts/${id}/channels/${platform}/resolve-postiz`, {
        method: 'POST',
        body: resolution,
    });

export const regenerateSocialPost = id =>
    socialRequest(`/api/social-posts/${id}/regenerate`, { method: 'POST' });

export const cancelSocialPost = id =>
    socialRequest(`/api/social-posts/${id}/cancel`, { method: 'POST' });

export const getSocialSettings = () => socialRequest('/api/social/settings');

export const updateSocialSettings = settings =>
    socialRequest('/api/social/settings', { method: 'PUT', body: settings });

// Health + account pickers wait on EXTERNAL providers server-side (Postiz 30s /
// Storrito 15s HTTP timeouts, fault-isolated per provider) — budget above their
// worst case so a slow provider degrades into its health row, not a client abort.
export const getSocialHealth = () => socialRequest('/api/social/health', { timeoutMs: 45000 });

export const getSocialStorritoAccounts = () =>
    socialRequest('/api/social/storrito/accounts', { timeoutMs: 45000 });

export const getSocialExtensionKey = () => socialRequest('/api/social/extension/key');

export const rotateSocialExtensionKey = () =>
    socialRequest('/api/social/extension/key/rotate', { method: 'POST' });

export const getSocialTriggers = () => socialRequest('/api/social/triggers');

export const getSocialWorkflows = () => socialRequest('/api/social/workflows');

// Create a blank, paused workflow (one unset Auslöser). The client navigates
// straight into the studio at the returned slug.
export const createSocialWorkflow = name =>
    socialRequest('/api/social/workflows', { method: 'POST', body: { name } });

// Load ONE workflow by its permanent 4-letter slug (the studio loader).
export const getSocialWorkflowBySlug = slug => socialRequest(`/api/social/workflows/${slug}`);

// Autosave the studio's working graph as this workflow's DRAFT. Shape-checked
// only server-side — publishing is the strict validate gate.
export const saveSocialWorkflowDraft = (slug, graph) =>
    socialRequest(`/api/social/workflows/${slug}/draft`, { method: 'PUT', body: { graph } });

// Publish the draft: THE strict gate (400 with a German message on an invalid
// graph). Re-derives the trigger and clears the draft.
export const publishSocialWorkflow = slug =>
    socialRequest(`/api/social/workflows/${slug}/publish`, { method: 'POST' });

// "Zurücksetzen": discard the draft, back to the published state.
export const discardSocialWorkflowDraft = slug =>
    socialRequest(`/api/social/workflows/${slug}/discard-draft`, { method: 'POST' });

// Duplicate a workflow's published graph into a new, paused workflow.
export const duplicateSocialWorkflow = (slug, name) =>
    socialRequest(`/api/social/workflows/${slug}/duplicate`, { method: 'POST', body: { name } });

export const setSocialWorkflowEnabled = (slug, enabled) =>
    socialRequest(`/api/social/workflows/${slug}/enabled`, {
        method: 'PUT',
        body: { enabled },
    });

export const renameSocialWorkflow = (slug, name) =>
    socialRequest(`/api/social/workflows/${slug}/name`, {
        method: 'PUT',
        body: { name },
    });

// Hard-delete a workflow (409 while posts it minted are still in flight).
export const deleteSocialWorkflow = slug =>
    socialRequest(`/api/social/workflows/${slug}`, { method: 'DELETE' });

// Recent trigger decisions for the "Aktivität" panel (optionally scoped to one
// workflow by slug). Read-only; drives the decision copy in triggerLogCopy.js.
export const getSocialTriggerLog = (workflow, limit = 20) =>
    socialRequest(
        `/api/social/trigger-log?limit=${limit}${workflow ? `&workflow=${workflow}` : ''}`
    );

export const getSocialCatalog = () => socialRequest('/api/social/catalog');

export const getSocialTriggerEntities = (trigger, q = '', page = 1) =>
    socialRequest(
        `/api/social/triggers/${encodeURIComponent(trigger)}/entities?q=${encodeURIComponent(
            q
        )}&page=${page}`
    );

// Start a Testlauf as a server-side background JOB (202 + jobId) — it runs with
// the exact budgets of a real trigger run (an LLM attempt may take minutes), so
// the result arrives via polling, never a long-held request.
export const simulateSocialWorkflow = ({ trigger, graph, entityId, listingId }) =>
    socialRequest('/api/social/test/simulate', {
        method: 'POST',
        body: { trigger, graph, entityId: entityId || listingId },
    });

export const getSocialSimulation = jobId =>
    socialRequest(`/api/social/test/simulate/${encodeURIComponent(jobId)}`);

export const runManualSocialWorkflow = ({ slug, entityId, listingId }) =>
    socialRequest('/api/social/runs/manual', {
        method: 'POST',
        body: { slug, entityId: entityId || listingId },
    });

// ---- Self-serve brand-asset library (admin) ----

// A multipart sibling of socialRequest: same credentials + the same end-to-end
// deadline + the same loud error handling (it shares timedSocialFetch, so the
// body-timeout guarantee can never hold for one and not the other), but the body
// is a FormData — the browser sets the multipart Content-Type + boundary itself,
// we must NOT — so an uploaded file streams as a real file field multer reads.
const socialUpload = (path, formData) =>
    timedSocialFetch(
        `${apiBaseUrl()}${path}`,
        {
            method: 'POST',
            credentials: isLocalhost() ? 'omit' : 'include',
            body: formData,
        },
        SOCIAL_REQUEST_TIMEOUT_MS
    );

export const getSocialBrandAssets = () => socialRequest('/api/social/assets');

export const uploadSocialBrandAsset = (file, label) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('label', label);
    return socialUpload('/api/social/assets', formData);
};

export const deleteSocialBrandAsset = id =>
    socialRequest(`/api/social/assets/${id}`, { method: 'DELETE' });
